import { TitleSpan } from '@/components/features/shared/TitleSpan'
import { PriceLabel } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader } from '@/components/ui/dialog'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Account, AccountStatus, editAccountSchema, EditAccountValues, formatAccountStatus } from '@/domain/accounts/account'
import { accountsApi } from '@/rest/modules/accounts-api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { accountKeys } from '@/hooks/api/useAccounts'
import { useToast } from '@/hooks/use-toast'

interface EditAccountModalProps {
  isOpen: boolean
  onClose: () => void 
  account: Account | null
}

export const EditAccountModal = ({
  isOpen,
  onClose,
  account,
}: EditAccountModalProps) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const form = useForm<EditAccountValues>({
    resolver: zodResolver(editAccountSchema),
    defaultValues: {
      accountAlias: '',
      status: AccountStatus.ACTIVE,
    },
  })

  useEffect(() => {
    if (account) {
      form.reset({
        accountAlias: account.accountAlias || '',
        status: account.status,
      })
    }
  }, [account, form])

  const updateMutation = useMutation({
    mutationFn: (data: EditAccountValues) =>
      accountsApi.updateAccount(account!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all })
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(account!.id) })
      toast({
        title: 'Exito',
        description: 'Cuenta actualizada correctamente',
        variant: 'success',
      })
      onClose()
    },
    onError: (error: { message: string }) => {
      toast({
        title: 'Error',
        description: error.message || 'Error al actualizar la cuenta',
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = (values: EditAccountValues) => {
    updateMutation.mutate(values)
  }

  if (!account) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <CardTitle className='text-xl'>Editar Cuenta</CardTitle>
          <CardDescription>
            <h3 className="text-md font-bold flex items-center gap-2">
              {account.accountNumber} - {account.accountAlias || 'Principal'}
            </h3>
          </CardDescription>
        </DialogHeader>

        <CardContent>
          <Card className="bg-muted/30">
            <CardContent className="p-4 flex items-start gap-10">
              <div className="flex flex-col gap-1">
                <TitleSpan>Saldo Actual</TitleSpan>
                <div className={`text-lg font-bold ${account.currentBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <PriceLabel price={account.currentBalance} />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <TitleSpan>Estado</TitleSpan>
                <Badge className={"w-fit"}>
                  {formatAccountStatus(account.status)}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 mt-4">
              <FormField
                control={form.control}
                name="accountAlias"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Alias de la Cuenta</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Ej: Caja Principal"
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormDescription>Nombre descriptivo para la cuenta</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Activa</SelectItem>
                        <SelectItem value="suspended">Suspendida</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <div className='flex justify-between w-full'>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={updateMutation.isPending}
                  >
                    <X className="h-3.5 w-3.5 mr-1.5" />
                    Cancelar
                  </Button>
                  <Button
                    variant="info"
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={updateMutation.isPending}
                  >
                    Guardar cambios
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </CardContent>
      </DialogContent>
    </Dialog>
  )
}
